// The modal a handler='payment' action button opens.
//
// Mounted imperatively from `designerTxnRuntime`, like the Assistant modal,
// so every surface that builds a TxnRuntime — live preview, the in-app Run
// view, site embeds — gets it without threading modal state through each
// host.
//
// It is deliberately thin. The gateway dance, the verification round-trip
// and the settle-wait all live in the shared `startPayment` (page-runtime),
// which the storefront's `payment_checkout` block uses too; this component
// is the confirm-and-report shell around it. What it must get right is what
// it SAYS at each stage, because "did my money go?" is the one question a
// payment UI has to answer honestly.

import { useCallback, useEffect, useState } from 'react'
import {
  awaitSettlement, fetchPaymentConfig, PaymentCancelled, placeCashOnDelivery,
  startPayment,
  type CashOnDeliveryResult, type CheckoutOrder, type PaymentApi,
  type PaymentPublicConfig,
} from '@twasta/page-runtime'
// The action-button request shape lives with the txn contracts, not with
// the checkout transport.
import type { PaymentActionRequest } from '@twasta/page-runtime/txn'
import { getSiteToken } from '@/api/website'

const APP_BASE = (import.meta.env?.BASE_URL || '/').replace(/\/$/, '')

function makeApi(ownerId: string, project: string): PaymentApi {
  const token = getSiteToken(ownerId, project)
  return {
    base: `${APP_BASE}/api/app-payments/${encodeURIComponent(ownerId)}`
          + `/${encodeURIComponent(project)}`,
    fetch: (url, init) => fetch(url, {
      ...init,
      credentials: 'include',
      headers: {
        ...(init?.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }),
  }
}

export function PaymentActionModal({ project, ownerId, req, onClose }: {
  project: string
  ownerId: string
  req: PaymentActionRequest
  onClose: () => void
}) {
  const [config, setConfig] = useState<PaymentPublicConfig | null>(null)
  const [busy, setBusy] = useState(false)
  const [order, setOrder] = useState<CheckoutOrder | null>(null)
  const [pending, setPending] = useState(false)
  const [cod, setCod] = useState<CashOnDeliveryResult | null>(null)
  const [error, setError] = useState('')
  const [loadError, setLoadError] = useState('')

  const api = ownerId && project ? makeApi(ownerId, project) : null

  useEffect(() => {
    if (!api) { setLoadError('This surface does not know which application to charge for.'); return }
    let alive = true
    fetchPaymentConfig(api)
      .then(cfg => { if (alive) setConfig(cfg) })
      .catch(err => { if (alive) setLoadError(err instanceof Error ? err.message : 'Could not read the payment setup.') })
    return () => { alive = false }
    // The api object is rebuilt each render but is value-stable for a given
    // (owner, project); depending on it would re-fetch on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, project])

  const pay = useCallback(async () => {
    if (!api) return
    setError('')
    setBusy(true)
    try {
      const result = await startPayment(api, {
        objName: req.objName, txnId: req.txnId,
        binding: req.binding as unknown as Record<string, unknown>,
        description: req.title,
      })
      setOrder(result.order)
      setPending(result.pending)
      if (result.pending) {
        const settled = await awaitSettlement(api, result.order.id)
        if (settled) { setOrder(settled); setPending(settled.status !== 'paid') }
      }
    } catch (err) {
      // A closed gateway window is a decision, not a failure — say nothing
      // and leave the modal as it was so they can try again.
      if (err instanceof PaymentCancelled) setError('')
      else setError(err instanceof Error ? err.message : 'The payment could not be completed.')
    } finally {
      setBusy(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, project, req])

  // Cash on delivery through the payment rail — the record's payment columns
  // are writable only there. The status stamped is the action's
  // `cod_status_value`, which the server reads from the model.
  const placeCod = useCallback(async () => {
    if (!api) return
    setError('')
    setBusy(true)
    try {
      setCod(await placeCashOnDelivery(api, {
        objName: req.objName, txnId: req.txnId,
        binding: req.binding as unknown as Record<string, unknown>,
      }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cash on delivery could not be recorded.')
    } finally {
      setBusy(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, project, req])

  const paid = order?.status === 'paid'
  const offerCod = !!config?.cod_enabled && !!req.binding?.cod_status_value

  return (
    <div
      role="dialog" aria-modal="true" aria-label={req.title}
      style={{
        position: 'fixed', inset: 0, zIndex: 3000, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 24,
        background: 'rgba(15,23,42,0.55)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose() }}
    >
      <div style={{
        background: 'var(--color-bg-secondary, #fff)', borderRadius: 12,
        width: 'min(460px, 100%)', padding: 24, display: 'flex',
        flexDirection: 'column', gap: 14,
        boxShadow: '0 16px 56px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{req.title}</h2>
          <button className="btn btn-xs btn-ghost" onClick={onClose} disabled={busy}
                  aria-label="Close">✕</button>
        </div>

        {loadError && (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--color-error, #dc2626)' }}>
            {loadError}
          </p>
        )}

        {cod ? (
          <>
            <p style={{ margin: 0, fontSize: 15 }}>
              <strong style={{ color: 'var(--color-success, #16a34a)' }}>
                Order confirmed.
              </strong>{' '}
              {`${cod.reference || 'This order'} will be paid in cash on delivery.`}
            </p>
            <button className="btn btn-sm" onClick={onClose}>Done</button>
          </>
        ) : paid ? (
          <>
            <p style={{ margin: 0, fontSize: 15 }}>
              <strong style={{ color: 'var(--color-success, #16a34a)' }}>
                Payment received.
              </strong>{' '}
              {order?.reference ? `${order.reference} is paid.` : ''}
            </p>
            {order && !order.written_back && (
              // The money moved and the record did not change. Saying so is
              // the only honest option: the operator needs to know before
              // they look at the order and see "unpaid".
              <p style={{ margin: 0, fontSize: 14, color: 'var(--color-warning, #d97706)' }}>
                The record has not been marked paid yet — an administrator can
                finish it from Advanced → Payments.
              </p>
            )}
            <button className="btn btn-sm" onClick={onClose}>Done</button>
          </>
        ) : (
          <>
            {config && !config.enabled && !offerCod && (
              <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-secondary)' }}>
                {config.configured
                  ? 'This application’s payment gateway is configured but switched off.'
                  : 'This application has no payment gateway connected yet. '
                    + 'Set one up in Advanced → Payments.'}
              </p>
            )}
            {config?.enabled && (
              <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-secondary)' }}>
                You will be taken to {config.provider === 'stripe' ? 'Stripe' : 'Razorpay'}
                {config.mode === 'test' ? ' (test mode — no real money moves)' : ''}.
                The amount is taken from the saved record.
              </p>
            )}
            {pending && (
              <p style={{ margin: 0, fontSize: 14, color: 'var(--color-warning, #d97706)' }}>
                Your bank approved it and we are still confirming. Do not pay
                again — this will settle on its own.
              </p>
            )}
            {error && (
              <p style={{ margin: 0, fontSize: 14, color: 'var(--color-error, #dc2626)' }}>
                {error}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              {(config?.enabled || !offerCod) && (
                <button className="btn btn-sm" onClick={() => void pay()}
                        disabled={busy || !config?.enabled}>
                  {busy ? 'Opening the payment window…' : 'Pay now'}
                </button>
              )}
              {offerCod && (
                <button className="btn btn-sm" onClick={() => void placeCod()}
                        disabled={busy}>
                  {busy ? 'Confirming…' : 'Cash on delivery'}
                </button>
              )}
              <button className="btn btn-sm btn-ghost" onClick={onClose} disabled={busy}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default PaymentActionModal
